/**
 * Custom Safari Management Component
 * Admin dashboard to view all carts, scheduled safaris, and bookings
 */

import React, { useState, useEffect } from 'react';
import {
  ShoppingCartIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserIcon,
  CurrencyDollarIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../../services/apiService';

// For admin queries, we need to bypass RLS or use service role
// For now, we'll use the regular client and handle errors gracefully
// In production, consider using service role key for admin operations
import { useTheme } from '../../contexts/ThemeContext';
import { CustomSafariItinerary } from '../../types/customSafari';
import { useToast } from '../../contexts/ToastContext';

interface CartItem {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  itinerary: CustomSafariItinerary;
  status: string;
  saved_at: string;
  scheduled_date?: string;
  notes?: string;
}

interface ScheduledSafari {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  itinerary: CustomSafariItinerary;
  scheduled_start_date: string;
  scheduled_end_date: string;
  participants: number;
  status: string;
  created_at: string;
}

interface Booking {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  itinerary: CustomSafariItinerary;
  total_price: number;
  currency: string;
  booking_status: string;
  payment_status: string;
  created_at: string;
}

const CustomSafariManagement: React.FC = () => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const { actualTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<'carts' | 'scheduled' | 'bookings'>('carts');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [scheduledSafaris, setScheduledSafaris] = useState<ScheduledSafari[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'carts') {
        await loadCarts();
      } else if (activeTab === 'scheduled') {
        await loadScheduledSafaris();
      } else {
        await loadBookings();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCarts = async () => {
    try {
      // Get all cart items with user information
      const data = await apiService.request<any[]>('/custom-safari-carts/carts');

      const items: CartItem[] = (data || []).map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        user_name: item.user_profiles?.name || 'Unknown',
        user_email: item.user_profiles?.email || 'No email',
        user_phone: item.user_profiles?.phone || 'No phone',
        itinerary: item.itinerary,
        status: item.status,
        saved_at: item.saved_at,
        scheduled_date: item.scheduled_date,
        notes: item.notes,
      }));

      setCartItems(items);
    } catch (error) {
      console.error('Error loading carts:', error);
    }
  };

  const loadScheduledSafaris = async () => {
    try {
      const data = await apiService.request<any[]>('/custom-safari-carts/scheduled');

      const items: ScheduledSafari[] = (data || []).map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        user_name: item.user_profiles?.name || 'Unknown',
        user_email: item.user_profiles?.email || 'No email',
        user_phone: item.user_profiles?.phone || 'No phone',
        itinerary: item.itinerary,
        scheduled_start_date: item.scheduled_start_date,
        scheduled_end_date: item.scheduled_end_date,
        participants: item.participants,
        status: item.status,
        created_at: item.created_at,
      }));

      setScheduledSafaris(items);
    } catch (error) {
      console.error('Error loading scheduled safaris:', error);
    }
  };

  const loadBookings = async () => {
    try {
      const data = await apiService.request<any[]>('/custom-safari-carts/bookings');

      const items: Booking[] = (data || []).map((item: any) => ({
        id: item.id,
        user_id: item.user_id,
        user_name: item.user_profiles?.name || 'Unknown',
        user_email: item.user_profiles?.email || 'No email',
        user_phone: item.user_profiles?.phone || 'No phone',
        itinerary: item.itinerary,
        total_price: item.total_price,
        currency: item.currency,
        booking_status: item.booking_status,
        payment_status: item.payment_status,
        created_at: item.created_at,
      }));

      setBookings(items);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const updateStatus = async (table: string, id: string, status: string) => {
    try {
      await apiService.request(`/custom-safari-carts/${table}/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });

      await loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Failed to update status', 'Update Error');
    }
  };

  const getStatusBadge = (status: string) => {

    const statusConfig: { [key: string]: { darkClass: string; lightClass: string; icon: any } } = {
      saved: {
        darkClass: 'bg-blue-900/30 text-blue-300',
        lightClass: 'bg-blue-50 text-blue-700',
        icon: ShoppingCartIcon
      },
      scheduled: {
        darkClass: 'bg-yellow-900/30 text-yellow-300',
        lightClass: 'bg-yellow-50 text-yellow-700',
        icon: CalendarIcon
      },
      pending: {
        darkClass: 'bg-yellow-900/30 text-yellow-300',
        lightClass: 'bg-yellow-50 text-yellow-700',
        icon: ClockIcon
      },
      confirmed: {
        darkClass: 'bg-green-900/30 text-green-300',
        lightClass: 'bg-green-50 text-green-700',
        icon: CheckCircleIcon
      },
      booked: {
        darkClass: 'bg-green-900/30 text-green-300',
        lightClass: 'bg-green-50 text-green-700',
        icon: CheckCircleIcon
      },
      cancelled: {
        darkClass: 'bg-red-900/30 text-red-300',
        lightClass: 'bg-red-50 text-red-700',
        icon: XCircleIcon
      },
      paid: {
        darkClass: 'bg-green-900/30 text-green-300',
        lightClass: 'bg-green-50 text-green-700',
        icon: CheckCircleIcon
      },
      partial: {
        darkClass: 'bg-orange-900/30 text-orange-300',
        lightClass: 'bg-orange-50 text-orange-700',
        icon: ClockIcon
      },
    };

    const config = statusConfig[status] || {
      darkClass: 'bg-slate-700 text-slate-300',
      lightClass: 'bg-neutral-100 text-neutral-700',
      icon: ClockIcon
    };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${actualTheme === 'dark' ? config.darkClass : config.lightClass
        }`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className={`rounded-lg ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white border border-neutral-200'
      } p-6`}>
      <div className="mb-6">
        <h2 className={`text-2xl font-bold mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
          }`}>
          Custom Safari Management
        </h2>
        <p className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
          }`}>
          View and manage all custom safari carts, scheduled safaris, and bookings
        </p>
      </div>

      {/* Tabs */}
      <div className={`flex gap-2 mb-6 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
        }`}>
        {[
          { id: 'carts', label: 'Carts', icon: ShoppingCartIcon, count: cartItems.length },
          { id: 'scheduled', label: 'Scheduled', icon: CalendarIcon, count: scheduledSafaris.length },
          { id: 'bookings', label: 'Bookings', icon: CheckCircleIcon, count: bookings.length },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors border-b-2 ${activeTab === tab.id
                  ? actualTheme === 'dark'
                    ? 'text-primary-400 border-primary-400'
                    : 'text-primary-800 border-primary-800'
                  : actualTheme === 'dark'
                    ? 'text-slate-400 border-transparent hover:text-slate-300'
                    : 'text-neutral-600 border-transparent hover:text-primary-700'
                }`}
            >
              <Icon className="h-5 w-5" />
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${actualTheme === 'dark' ? 'bg-slate-700 text-slate-300' : 'bg-neutral-100 text-neutral-700'
                }`}>
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
          }`}>
          Loading...
        </div>
      ) : (
        <div className="space-y-4">
          {activeTab === 'carts' && (
            <>
              {cartItems.length === 0 ? (
                <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  <ShoppingCartIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No items in cart</p>
                </div>
              ) : (
                cartItems.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-lg border ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600'
                        : 'bg-white border-neutral-200'
                      } p-5`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                          }`}>
                          {item.itinerary.name}
                        </h3>
                        <div className="flex items-center gap-4 mb-2">
                          <div className={`flex items-center gap-1.5 text-sm ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                            }`}>
                            <UserIcon className="h-4 w-4" />
                            <span>{item.user_name}</span>
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.user_email}
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.user_phone}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.itinerary.tours.length} tours • {item.itinerary.totalDuration} days
                          </div>
                          {getStatusBadge(item.status)}
                        </div>
                      </div>
                      <div className={`text-right ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
                        }`}>
                        <div className="text-xl font-bold">
                          {item.itinerary.totalPrice.currency} {item.itinerary.totalPrice.total.toLocaleString()}
                        </div>
                        <div className={`text-xs mt-1 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                          }`}>
                          Saved {new Date(item.saved_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-slate-700 dark:border-slate-600">
                      <button
                        onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-slate-600 hover:bg-slate-500 text-white'
                            : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                          }`}
                      >
                        {selectedItem?.id === item.id ? 'Hide Details' : 'View Details'}
                      </button>
                      <button
                        onClick={() => updateStatus('custom_safari_cart', item.id, 'scheduled')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-yellow-700 hover:bg-yellow-600 text-white'
                            : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                          }`}
                      >
                        Mark as Scheduled
                      </button>
                    </div>
                    {selectedItem?.id === item.id && (
                      <div className={`mt-4 p-4 rounded-lg ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-neutral-50'
                        }`}>
                        <div className="space-y-2 text-sm">
                          <div><strong>Tours:</strong> {item.itinerary.tours.map(t => t.tour.title).join(', ')}</div>
                          {item.notes && <div><strong>Notes:</strong> {item.notes}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'scheduled' && (
            <>
              {scheduledSafaris.length === 0 ? (
                <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  <CalendarIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No scheduled safaris</p>
                </div>
              ) : (
                scheduledSafaris.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-lg border ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600'
                        : 'bg-white border-neutral-200'
                      } p-5`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                          }`}>
                          {item.itinerary.name}
                        </h3>
                        <div className="flex items-center gap-4 mb-2">
                          <div className={`flex items-center gap-1.5 text-sm ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                            }`}>
                            <UserIcon className="h-4 w-4" />
                            <span>{item.user_name}</span>
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.user_email}
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.user_phone}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center gap-1.5 text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            <CalendarIcon className="h-4 w-4" />
                            <span>
                              {new Date(item.scheduled_start_date).toLocaleDateString()} - {new Date(item.scheduled_end_date).toLocaleDateString()}
                            </span>
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.participants} participant{item.participants !== 1 ? 's' : ''}
                          </div>
                          {getStatusBadge(item.status)}
                        </div>
                      </div>
                      <div className={`text-right ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
                        }`}>
                        <div className="text-xl font-bold">
                          {item.itinerary.totalPrice.currency} {item.itinerary.totalPrice.total.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-slate-700 dark:border-slate-600">
                      <button
                        onClick={() => updateStatus('scheduled_safaris', item.id, 'confirmed')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-green-700 hover:bg-green-600 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => updateStatus('scheduled_safaris', item.id, 'cancelled')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-red-700 hover:bg-red-600 text-white'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                          }`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'bookings' && (
            <>
              {bookings.length === 0 ? (
                <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  <CheckCircleIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>No bookings yet</p>
                </div>
              ) : (
                bookings.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-lg border ${actualTheme === 'dark'
                        ? 'bg-slate-700 border-slate-600'
                        : 'bg-white border-neutral-200'
                      } p-5`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                          }`}>
                          {item.itinerary.name}
                        </h3>
                        <div className="flex items-center gap-4 mb-2">
                          <div className={`flex items-center gap-1.5 text-sm ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                            }`}>
                            <UserIcon className="h-4 w-4" />
                            <span>{item.user_name}</span>
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.user_email}
                          </div>
                          <div className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                            {item.user_phone}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(item.booking_status)}
                          {getStatusBadge(item.payment_status)}
                        </div>
                      </div>
                      <div className={`text-right ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
                        }`}>
                        <div className="text-xl font-bold">
                          {item.currency} {item.total_price.toLocaleString()}
                        </div>
                        <div className={`text-xs mt-1 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                          }`}>
                          Booked {new Date(item.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-slate-700 dark:border-slate-600">
                      <button
                        onClick={() => updateStatus('custom_safari_bookings', item.id, 'confirmed')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-green-700 hover:bg-green-600 text-white'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                      >
                        Confirm Booking
                      </button>
                      <button
                        onClick={() => updateStatus('custom_safari_bookings', item.id, 'cancelled')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${actualTheme === 'dark'
                            ? 'bg-red-700 hover:bg-red-600 text-white'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                          }`}
                      >
                        Cancel Booking
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSafariManagement;

